import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, AlertCircle, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { adminInquiryApi, AdminInquiryListItem, InquiryStatus } from '@/api/inquiryApi';

const STATUS_LABEL: Record<InquiryStatus, string> = {
  PENDING: '답변대기',
  ANSWERED: '답변완료',
  CLOSED: '완료',
};

const STATUS_CLASS: Record<InquiryStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  ANSWERED: 'bg-green-100 text-green-700 border-green-300',
  CLOSED: 'bg-gray-100 text-gray-500 border-gray-300',
};

export default function AdminInquiryList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AdminInquiryListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'ALL'>('ALL');

  const PAGE_SIZE = 15;

  const loadList = (page: number, status: InquiryStatus | 'ALL' = statusFilter) => {
    setLoading(true);
    setError(null);
    const statusParam = status === 'ALL' ? undefined : status;
    adminInquiryApi.getList(page, PAGE_SIZE, statusParam)
      .then(res => {
        setItems(res.data.content ?? []);
        setTotalPages(res.data.totalPages ?? 0);
        setTotalElements(res.data.totalElements ?? 0);
        setCurrentPage(res.data.number ?? page);
        if (res.data.pendingCount !== undefined) setPendingCount(res.data.pendingCount);
      })
      .catch(() => setError('문의 목록을 불러오는데 실패했습니다.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadList(0, statusFilter); }, []);

  const handleFilterChange = (s: InquiryStatus | 'ALL') => {
    setStatusFilter(s);
    loadList(0, s);
  };

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ko-KR') : '-';

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            1:1 문의 관리
            {pendingCount > 0 && (
              <Badge className="bg-red-500 text-white text-xs">{pendingCount}건 대기</Badge>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">사용자 문의를 확인하고 답변합니다.</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 필터 탭 */}
      <div className="flex gap-2 flex-wrap">
        {(['ALL', 'PENDING', 'ANSWERED', 'CLOSED'] as const).map(s => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange(s)}
          >
            {s === 'ALL' ? '전체' : STATUS_LABEL[s as InquiryStatus]}
            {s === 'PENDING' && pendingCount > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-xs rounded-full px-1.5 py-0 leading-5">
                {pendingCount}
              </span>
            )}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium text-gray-700">
            총 {totalElements.toLocaleString()}건
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-7 h-7 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-16 text-center whitespace-nowrap">번호</TableHead>
                  <TableHead>제목</TableHead>
                  <TableHead className="w-32">사용자</TableHead>
                  <TableHead className="w-24 text-center">상태</TableHead>
                  <TableHead className="w-28 text-center">등록일</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-400 py-16">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      해당하는 문의가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : items.map((item, idx) => (
                  <TableRow
                    key={item.id}
                    className={`cursor-pointer hover:bg-blue-50/50 transition-colors ${item.status === 'PENDING' ? 'font-medium' : ''}`}
                    onClick={() => navigate(`/dashboard/admin/inquiries/${item.id}`)}
                  >
                    <TableCell className="text-center text-gray-400 text-sm">
                      {totalElements - (currentPage * PAGE_SIZE + idx)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {item.isSecret && <span className="text-gray-400 text-xs">🔒</span>}
                        <span className="text-gray-800 line-clamp-1">{item.title}</span>
                        {item.status === 'PENDING' && (
                          <Badge className="bg-yellow-500 text-white text-xs px-1.5 py-0 h-5 flex-shrink-0">미답변</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm text-gray-700">{item.userEmail}</p>
                        {item.userName && <p className="text-xs text-gray-400">{item.userName}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={STATUS_CLASS[item.status]}>
                        {STATUS_LABEL[item.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm text-gray-500">
                      {formatDate(item.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-1">
          <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => loadList(currentPage - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i}
              variant={i === currentPage ? 'default' : 'outline'}
              size="sm" className="w-9"
              onClick={() => loadList(i)}
            >
              {i + 1}
            </Button>
          ))}
          <Button variant="outline" size="sm" disabled={currentPage === totalPages - 1} onClick={() => loadList(currentPage + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
